import React, { useEffect, useMemo, useState, useCallback } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import toast from 'react-hot-toast';
import api from '../../api';
import { Check, X, User, Mail, Clock } from 'lucide-react';
import ListPageHeader from '../../components/list/ListPageHeader';
import ListPagination from '../../components/list/ListPagination';
import ListEmptyState from '../../components/list/ListEmptyState';
import ListLoadingState from '../../components/list/ListLoadingState';
import DataRow, { DataRowList } from '../../components/list/DataRow';
import StatusPill from '../../components/list/StatusPill';
import { parsePaginated, DEFAULT_LIST_PAGE_SIZE } from '../../utils/listHelpers';

function PendingStudents() {
  const [students, setStudents] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LIST_PAGE_SIZE);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const fetchPendingStudents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/auth/users/students/', { params: { status: 'pending', page, page_size: pageSize } });
      const { results, count } = parsePaginated(res);
      setStudents(results);
      setTotalCount(count);
    } catch (e) {
      toast.error('Failed to load pending students.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchPendingStudents();
  }, [fetchPendingStudents]);

  const handleApprove = async (id) => {
    try {
      setActionLoadingId(id);
      await api.post(`/auth/users/${id}/approve/`, {});
      toast.success('Student approved');
      await fetchPendingStudents();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to approve student.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReject = async (id) => {
    try {
      setActionLoadingId(id);
      await api.post(`/auth/users/${id}/reject/`, {});
      toast.success('Student rejected');
      await fetchPendingStudents();
    } catch (e) {
      toast.error('Failed to reject student.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const heading = useMemo(() => `Pending Students (${totalCount})`, [totalCount]);

  return (
    <DashboardLayout>
      <main className="page-container content-width ux-list-page">
        <ListPageHeader
          kicker="Admissions"
          kickerIcon={Clock}
          title={heading}
          description="Approve or reject pending student registrations."
        />

        {loading ? (
          <ListLoadingState label="Loading pending students…" />
        ) : students.length === 0 ? (
          <ListEmptyState icon={User} title="No pending students" description="All student registrations have been processed." />
        ) : (
          <div className="ux-table-shell">
            <DataRowList>
              {students.map((s, index) => {
                const fullName = s.first_name || s.last_name
                  ? `${s.first_name ?? ''} ${s.last_name ?? ''}`.trim()
                  : (s.username || 'Student');
                return (
                  <DataRow
                    key={s.id}
                    index={index}
                    leading={(
                      <div className="ux-avatar">
                        <User size={16} />
                      </div>
                    )}
                    title={fullName}
                    subtitle={(
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <Mail size={12} /> {s.email}
                      </span>
                    )}
                    meta={<span>{s.campus_name || '—'}</span>}
                    badges={<StatusPill status="pending" label="Pending" />}
                    actions={(
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        <button
                          type="button"
                          className="ux-icon-btn danger"
                          onClick={() => handleReject(s.id)}
                          disabled={actionLoadingId === s.id}
                          title="Reject"
                        >
                          <X size={16} />
                        </button>
                        <button
                          type="button"
                          className="ux-icon-btn success"
                          onClick={() => handleApprove(s.id)}
                          disabled={actionLoadingId === s.id}
                          title="Approve"
                        >
                          <Check size={16} />
                        </button>
                      </div>
                    )}
                  />
                );
              })}
            </DataRowList>
          </div>
        )}

        {!loading && students.length > 0 && (
          <ListPagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
          />
        )}
      </main>
    </DashboardLayout>
  );
}

export default PendingStudents;
